import { Box, Chip, Container, Typography } from "@mui/material"
import { IApiFunc, IApiResult } from "auth/interface/api.interface"
import { useApi } from "auth/useApi3"
import ChipTooltip from "components/ChipTooltip/ChipTooltip"
import ClpTable from "components/ClpTable"
import { ClpTableEnum } from "components/ClpTable/ClpTableEnum"
import { GlobalLoaderContext } from "components/GlobalLoadingProvider"
import { apiEndpoints } from "generated/apiEndpoints"
import moment from "moment"
import { useContext, useEffect } from "react"
import { useTranslation } from "react-i18next"
import { StyledTableContainer } from "shared/styled/Global"

const Bkychecks = () => {
  const { t } = useTranslation()
  const [bkychecksTemplatesResult, bkychecksTemplatesApi] = useApi() as [IApiResult, IApiFunc]
  const loadingContext = useContext(GlobalLoaderContext)

  useEffect(() => {
    bkychecksTemplatesApi.get(apiEndpoints.reporting.getuserbkyreport)
  }, [])

  useEffect(() => {
    loadingContext.setLoading(() => bkychecksTemplatesResult.status === 1)
  }, [])

  const columns2: any[] = [
    {
      accessorKey: "name",
      header: t("global.name", "Name")
    },
    {
      accessorKey: "socialSecurityNumber",
      header: t("global.date_of_birth", "Date of Birth")
    },
    {
      accessorKey: "managerName",
      header: t("global.manager", "Manager")
    },
    {
      id: "updatedTime",
      accessorFn: (row: any) => moment.utc(row.updatedTime).local().format("YYYY-MM-DD HH:mm"),
      header: t("global.date", "Date")
    },
    {
      id: "drivingLicensQualifications",
      accessorFn: (row: any) => row?.drivingLicensQualifications?.join(", ") ?? "",
      header: t("global.driver_licenses_permissions", "Driving license privileges"),
      meta: {
        tooltip: t("settings.user.shows_active_company_licenses", "Visar körkortsbehörigheter för aktiverade kategorier.")
      },
      cell: ({ row }: any) => {
        const drivingLicensQualifications = row.original.drivingLicensQualifications
        const visible = drivingLicensQualifications.slice(0, 3)
        const remaining = drivingLicensQualifications.slice(3)

        if (!drivingLicensQualifications) return null

        return (
          <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap", justifyContent: { xs: "flex-end", xl: "flex-start" } }}>
            {visible.map((data: any) => (
              <Chip
                sx={{ minWidth: "30px" }}
                key={data}
                label={data}
                size="small"
                variant="outlined"
              />
            ))}

            {remaining.length > 0 && (
              <ChipTooltip
                label={`+${remaining.length} ${t("global.to", "to")}`}
                remaining={remaining.map((data: any) => data).join(", ")}
              />
            )}
          </Box>
        )
      },
    },
    {
      id: "approvedAccordingToBky",
      accessorFn: (row: any) => row.validLicences?.join(", ") || "",
      header: t("settings.user.approved_according_to_bky", "Approved according to BKY"),
      meta: {
        tooltip: t("settings.user.approved_according_to_bky_last_check", "Authorizations that have been verified against BKY at the last check.")
      },
      cell: ({ row }: any) => {
        const validLicences = row.original.validLicences
        const visible = validLicences.slice(0, 3)
        const remaining = validLicences.slice(3)

        if (!validLicences) return null

        return (
          <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap", justifyContent: { xs: "flex-end", xl: "flex-start" } }}>
            {visible.map((data: any) => (
              <Chip
                sx={{ minWidth: "30px" }}
                key={data}
                label={data}
                size="small"
                variant="outlined"
                color="success"

              />
            ))}

            {remaining.length > 0 && (
              <ChipTooltip
                color="success"
                label={`+${remaining.length} ${t("global.to", "to")}`}
                remaining={remaining.map((data: any) => data).join(", ")}
              />
            )}
          </Box>
        )
      },
    },
    {
      id: "missingAccordingToBky",
      accessorFn: (row: any) => row.invalidLicences?.map((item: string) => item).join(", ") || "",
      header: t("settings.user.missing_according_to_bky", "Missing according to BKY"),
      meta: {
        tooltip: t("settings.user.authorizations_that_exist", "Authorizations that exist in the system but are not confirmed by BKY at the last check.")
      },
      cell: ({ row }: any) => {
        const invalidLicences = row.original.invalidLicences
        const visible = invalidLicences.slice(0, 3)
        const remaining = invalidLicences.slice(3)

        if (!invalidLicences) return null

        return (
          <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap", justifyContent: { xs: "flex-end", xl: "flex-start" } }}>
            {visible.map((data: any) => (
              <Chip
                sx={{ minWidth: "30px" }}
                key={data}
                label={data}
                size="small"
                variant="outlined"
                color="error"

              />
            ))}

            {remaining.length > 0 && (
              <ChipTooltip
                color="error"
                label={`+${remaining.length} ${t("global.to", "to")}`}
                remaining={remaining.map((data: any) => data).join(", ")}
              />
            )}
          </Box>
        )
      },
    },
  ]

  return (
    <div>
      <Container>
        <Box>
          <Typography variant="h1">{t("global.features.title.bkychecks", "BKY-checks")}</Typography>
        </Box>
      </Container>

      <StyledTableContainer>
        <ClpTable
          defaultSorting={[
            { id: "missingAccordingToBky", desc: true },
            { id: "date", desc: true },
          ]}
          columns={columns2}
          rows={bkychecksTemplatesResult.status === 2 && bkychecksTemplatesResult.data}
          loadingState={bkychecksTemplatesResult.status}
          tableId={ClpTableEnum.UserReportsBkyChecks}
        />
      </StyledTableContainer>
    </div>
  )
}

export default Bkychecks
